import { Component, TemplateRef, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { GenderEnum, ReservationTourist } from '@appTypes/api.types';
import {
  maxDate,
  maxDateThanField,
  maxLengthWithMessage,
  minDate,
  regExpPattern,
  TOURIST_NAME_LETTER_MESSAGE,
  TOURIST_NAME_LETTER_PATTERN,
  TOURIST_NAME_PATTERN
} from '@core/misc/validators.utils';
import { TranslatePipe } from '@ngx-translate/core';
import { dispatch, select } from '@ngxs/store';
import { DatePickerComponent } from '@shared/components/dates/date-picker/date-picker.component';
import { SelectorComponent } from '@shared/components/selector/selector.component';
import { InputTitleCaseDirective } from '@shared/directives/input-title-case.directive';
import { KzInputDirective } from '@shared/directives/kz-input.directive';
import { DynamicModalData, OnDynamicModal } from '@shared/modules/dynamic-modal/dynamic-modal.types';
import { addMonths, subYears } from 'date-fns';
import { appGetCountries } from '../../../../../state/app.actions';
import { AppState } from '../../../../../state/app.state';

@Component({
  selector: 'app-res-tourist-edit-form',
  imports: [
    SelectorComponent,
    TranslatePipe,
    KzInputDirective,
    InputTitleCaseDirective,
    ReactiveFormsModule,
    DatePickerComponent,
    MatButton
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './res-tourist-edit-form.component.html'
})
export class ResTouristEditFormComponent implements OnDynamicModal<ReservationTourist> {
  readonly modalFooterTemplateRef = viewChild.required<TemplateRef<unknown>>('modalFooterTemplateRef');

  public onDynamicModalClose!: () => void;

  public readonly formGroup = new FormGroup({
    gender: new FormControl<GenderEnum | null>(null, [Validators.required]),
    firstName: new FormControl<string | null>(null, [
      Validators.required,
      regExpPattern(TOURIST_NAME_LETTER_PATTERN, TOURIST_NAME_LETTER_MESSAGE),
      regExpPattern(TOURIST_NAME_PATTERN, 'Only Latin letters, space, dot, apostrophe and hyphen are allowed'),
      maxLengthWithMessage(150, 'A name may have at most 150 characters')
    ]),
    lastName: new FormControl<string | null>(null, [
      Validators.required,
      regExpPattern(TOURIST_NAME_LETTER_PATTERN, TOURIST_NAME_LETTER_MESSAGE),
      regExpPattern(TOURIST_NAME_PATTERN, 'Only Latin letters, space, dot, apostrophe and hyphen are allowed'),
      maxLengthWithMessage(150, 'A name may have at most 150 characters')
    ]),
    birthDate: new FormControl<string | null>(null, [
      Validators.required,
      maxDate(),
      minDate(subYears(new Date(), 101), 'Age must be lower than 100'),
      maxDateThanField('passportIssue', 'The date must be before passport issue date')
    ]),
    citizenshipCountryCode: new FormControl<string | null>(null, Validators.required),
    passportNumber: new FormControl<string | null>(null, [
      Validators.required,
      regExpPattern(
        /^[0-9 A-Za-z]{5,50}$/,
        'Passport number must be between 5 and 50 characters, using only alphanumeric characters'
      )
    ]),
    passportExpiryDate: new FormControl<string | null>(null, [
      Validators.required,
      minDate(addMonths(new Date(), 6), 'Expiry date must be more than 6 months from today')
    ])
  });

  public readonly genderOptions = [
    { value: GenderEnum.Male, label: 'Male' },
    { value: GenderEnum.Female, label: 'Female' }
  ];

  public onDynamicModal(data: DynamicModalData<ReservationTourist>): void {
    this.#initData(data);
  }

  readonly countries = select(AppState.countries);
  readonly #getCountries = dispatch(appGetCountries);

  #initData(data: ReservationTourist) {
    this.formGroup.patchValue(data);
    this.#getCountries();
  }
}
